import {
    scheduleStepDisplayLabel,
    type ScheduleStepLabelSource,
} from "@/features/entries/services/timingLabelDerivation";
import { getDiaryCopy } from "@/features/i18n/diaryCopy";
import type { ReferenceDateSetupCopy } from "@/features/i18n/setupCopy";
import { getSetupCopy } from "@/features/i18n/setupCopy";
import type { ProcedureEventKind } from "@/features/diaries/utils/getProcedureInformationFromDiary";
import { DEFAULT_LOCALE, formatSide, type SupportedLocale } from "@patient-watch/i18n";
import type { ExtendedEventViewT, StatefulDiaryT, StatefulTrackerViewT } from "@patient-watch/types";
import { getSequenceBlockedReason } from "@/features/diaries/utils/sequentialFormHelpers";

const EVENT_HEADING_LABELS: Record<string, Partial<Record<SupportedLocale, string>>> = {
    Injection: {
        "de-DE": "Injektion",
        "da-DK": "Injektion",
        "fr-FR": "Injection",
        "it-IT": "Iniezione",
        "es-ES": "Inyección",
    },
    Surgery: {
        "de-DE": "Operation",
        "da-DK": "Operation",
        "fr-FR": "Opération",
        "it-IT": "Intervento",
        "es-ES": "Intervención",
    },
    "Shockwave Therapy": {
        "de-DE": "Stoßwellentherapie",
        "da-DK": "Stødbølgebehandling",
        "fr-FR": "Ondes de choc",
        "it-IT": "Onde d'urto",
        "es-ES": "Ondas de choque",
    },
    Consultation: {
        "de-DE": "Beratung",
        "da-DK": "Konsultation",
        "fr-FR": "Consultation",
        "it-IT": "Consulto",
        "es-ES": "Consulta",
    },
    Product: { "de-DE": "Produkt", "da-DK": "Produkt", "fr-FR": "Produit", "it-IT": "Prodotto", "es-ES": "Producto" },
    Procedure: {
        "de-DE": "Behandlung",
        "da-DK": "Behandling",
        "fr-FR": "Intervention",
        "it-IT": "Procedura",
        "es-ES": "Procedimiento",
    },
};

const PROCEDURE_PHRASE_LABELS: Record<string, Partial<Record<SupportedLocale, string>>> = {
    injection: {
        "de-DE": "Injektion",
        "da-DK": "injektion",
        "fr-FR": "injection",
        "it-IT": "iniezione",
        "es-ES": "inyección",
    },
    surgery: {
        "de-DE": "Operation",
        "da-DK": "operation",
        "fr-FR": "opération",
        "it-IT": "intervento",
        "es-ES": "intervención",
    },
    procedure: {
        "de-DE": "Behandlung",
        "da-DK": "behandling",
        "fr-FR": "intervention",
        "it-IT": "procedura",
        "es-ES": "procedimiento",
    },
    product: { "de-DE": "Produkt", "da-DK": "produkt", "fr-FR": "produit", "it-IT": "prodotto", "es-ES": "producto" },
    "shockwave therapy": {
        "de-DE": "Stoßwellentherapie",
        "da-DK": "stødbølgebehandling",
        "fr-FR": "ondes de choc",
        "it-IT": "onde d'urto",
        "es-ES": "ondas de choque",
    },
};

/** Mixed-case product names such as FlowOx - do not flatten to flowOx / Flow Ox. */
function isBrandWord(word: string): boolean {
    return /[a-z]/.test(word) && /[A-Z]/.test(word.slice(1));
}

const SLUG_DISPLAY_LABELS: Record<string, string> = {
    "msis-29": "MSIS-29",
    "flowox-device-training": "FlowOx Device Training",
    "ms-adl": "MS-ADL",
    "ms-profile": "MS Profile",
};

const SLUG_TOKEN_ALIASES: Record<string, string> = {
    flowox: "FlowOx",
    msis: "MSIS",
};

/** Official short name in parentheses, e.g. "Multiple Sclerosis Impact Scale (MSIS-29)". */
function shortLabelFromFormName(formName: string): string | null {
    const match = /\(([A-Z][A-Z0-9-]{1,20})\)/.exec(formName);
    return match?.[1] ?? null;
}

function formatTrackerTitle(
    formName: string | null | undefined,
    side: string | null | undefined,
    fallback: string,
    locale: SupportedLocale = DEFAULT_LOCALE
): string {
    // A form name is shown exactly as written. Never change its capitalisation.
    const name = formName?.trim() || fallback;
    if (side === "Left" || side === "Right" || side === "Both") {
        return `${name} - ${formatSide(side, locale)}`;
    }
    return name;
}

type TrackerDisplaySource = {
    form?: { form_name?: string | null; slug?: string | null } | null;
    side?: string | null;
};

function formatSlugAsTitle(slug: string): string {
    const known = SLUG_DISPLAY_LABELS[slug.toLowerCase()];
    if (known) return known;

    return slug
        .split("-")
        .filter((word) => word.length > 0)
        .map((word) => {
            const alias = SLUG_TOKEN_ALIASES[word.toLowerCase()];
            if (alias) return alias;
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(" ");
}

/**
 * Short form identity for CTAs. The official short name in parentheses when there is one (MSIS-29), otherwise
 * the form name exactly as written. Only a form with no name falls back to its slug, then to `fallback`.
 */
export function getFormSlugDisplayLabel(
    form: { slug?: string | null; form_name?: string | null } | null | undefined,
    fallback = "Questionnaire"
): string {
    const slug = form?.slug?.trim();
    const formName = form?.form_name?.trim();
    const shortName = formName ? shortLabelFromFormName(formName) : null;
    if (shortName) return shortName;
    if (formName) return formName;
    if (slug) return formatSlugAsTitle(slug);
    return fallback;
}

/** Questionnaire label from form name and laterality — never `tracker_name`. */
export function getTrackerDisplayLabel(
    tracker: TrackerDisplaySource | null | undefined,
    fallback = "Questionnaire",
    locale: SupportedLocale = DEFAULT_LOCALE
): string {
    return formatTrackerTitle(tracker?.form?.form_name, tracker?.side, fallback, locale);
}

/** Procedure label from event content — never stored `event_name`. */
export function getEventDisplayLabel(event: ExtendedEventViewT, locale: SupportedLocale = DEFAULT_LOCALE): string {
    if ((event.injections_view?.length ?? 0) > 0) {
        return EVENT_HEADING_LABELS.Injection?.[locale] ?? "Injection";
    }
    if ((event.surgeries_view?.length ?? 0) > 0) {
        const surgeryName = event.surgeries_view?.[0]?.surgery_name ?? "Surgery";
        return EVENT_HEADING_LABELS[surgeryName]?.[locale] ?? surgeryName;
    }
    if ((event.shockwaves_view?.length ?? 0) > 0) {
        return EVENT_HEADING_LABELS["Shockwave Therapy"]?.[locale] ?? "Shockwave Therapy";
    }
    if ((event.products_view?.length ?? 0) > 0) {
        const productName = event.products_view?.[0]?.product_name?.trim();
        return productName || EVENT_HEADING_LABELS.Product?.[locale] || "Product";
    }
    if ((event.educations_view?.length ?? 0) > 0) {
        const educationName = event.educations_view?.[0]?.education_name ?? "Consultation";
        return EVENT_HEADING_LABELS[educationName]?.[locale] ?? educationName;
    }
    return EVENT_HEADING_LABELS.Procedure?.[locale] ?? "Procedure";
}

/** Patient-facing label for a procedure reference date, e.g. "Date of ACL repair surgery". */
function isAcronymWord(word: string): boolean {
    const letters = word.replace(/[^A-Za-z]/g, "");
    return letters.length >= 2 && letters === letters.toUpperCase();
}

function formatProcedureNameForDateLabel(name: string): string {
    return name
        .split(" ")
        .map((word) =>
            isAcronymWord(word) || isBrandWord(word) ? word : `${word.charAt(0).toLowerCase()}${word.slice(1)}`
        )
        .join(" ");
}

export function getProcedureDateLabel(procedureHeading: string, locale: SupportedLocale = DEFAULT_LOCALE): string {
    const name = procedureHeading.trim();
    if (!name) {
        if (locale === "de-DE") return "Behandlungsdatum";
        if (locale === "da-DK") return "Behandlingsdato";
        if (locale === "fr-FR") return "Date de l'intervention";
        if (locale === "it-IT") return "Data dell'intervento";
        if (locale === "es-ES") return "Fecha de la intervención";
        return "Procedure date";
    }
    const phrase = getProcedurePhraseForPatientCopy(name, locale);
    if (locale === "de-DE") return `Datum der ${phrase}`;
    if (locale === "da-DK") return `Dato for ${phrase}`;
    if (locale === "fr-FR") return `Date ${/^[aeiouyhéèêàâîôû]/i.test(phrase) ? "d'" : "de "}${phrase}`;
    if (locale === "it-IT") return `Data di ${phrase}`;
    if (locale === "es-ES") return `Fecha de ${phrase}`;
    return `Date of ${phrase}`;
}

/** Lowercase procedure name for mid-sentence patient copy, e.g. "injection", "left knee arthroscopy". */
export function getProcedurePhraseForPatientCopy(
    procedureHeading: string,
    locale: SupportedLocale = DEFAULT_LOCALE
): string {
    const name = procedureHeading.trim();
    if (!name) {
        return PROCEDURE_PHRASE_LABELS.procedure?.[locale] ?? "procedure";
    }
    const formatted = formatProcedureNameForDateLabel(name);
    return PROCEDURE_PHRASE_LABELS[formatted]?.[locale] ?? formatted;
}

/** Status prefix for backup schedule entries, e.g. "Pre-injection · " / "Vor der Injektion · ". */
export function getPreProcedureStatusPrefix(
    procedureHeading: string | null | undefined,
    locale: SupportedLocale = DEFAULT_LOCALE
): string {
    const phrase = getProcedurePhraseForPatientCopy(procedureHeading?.trim() || "procedure", locale);
    if (locale === "de-DE") return `Vor der ${phrase} · `;
    if (locale === "da-DK") return `Før ${phrase} · `;
    if (locale === "fr-FR") return `Avant ${phrase} · `;
    if (locale === "it-IT") return `Prima di ${phrase} · `;
    if (locale === "es-ES") return `Antes de ${phrase} · `;
    return `Pre-${phrase} · `;
}

/** Completed status for backup schedule entries, e.g. "Pre-injection · completed". */
export function getPreProcedureCompletedStatus(
    procedureHeading: string | null | undefined,
    locale: SupportedLocale = DEFAULT_LOCALE
): string {
    const completed =
        locale === "de-DE"
            ? "abgeschlossen"
            : locale === "da-DK"
              ? "fuldført"
              : locale === "fr-FR"
                ? "terminé"
                : locale === "it-IT"
                  ? "completato"
                  : locale === "es-ES"
                    ? "completado"
                    : "completed";
    return `${getPreProcedureStatusPrefix(procedureHeading, locale)}${completed}`;
}

export type { ReferenceDateSetupCopy };

/** Prefer getSetupCopy(locale).getReferenceDateSetupCopy() or useSetupCopy().getReferenceDateSetupCopy(). */
export function getReferenceDateSetupCopy(
    procedureHeading: string | null | undefined,
    locale: SupportedLocale = DEFAULT_LOCALE,
    kind?: ProcedureEventKind
): ReferenceDateSetupCopy {
    return getSetupCopy(locale).getReferenceDateSetupCopy(procedureHeading, kind);
}

/** Diary context for navigation and headings — never `diary_name`. */
export function getDiaryContextLabel(diary: Pick<StatefulDiaryT, "patient_info" | "organisation_name">): string {
    const patient = diary.patient_info?.patient_name?.trim();
    if (patient) return patient;
    const org = diary.organisation_name?.trim();
    if (org) return org;
    return "Diary";
}

export function compareTrackersByDisplayLabel(a: StatefulTrackerViewT, b: StatefulTrackerViewT): number {
    return getTrackerDisplayLabel(a).localeCompare(getTrackerDisplayLabel(b));
}

function scheduleLabel(source: ScheduleStepLabelSource | null | undefined, locale: SupportedLocale): string | null {
    if (!source) return null;
    return scheduleStepDisplayLabel(source, locale);
}

/** Action-oriented card title for the patient To Do tab (distinct from Results form titles). */
export function getTrackerToDoCardHeadline(
    tracker: Pick<StatefulTrackerViewT, "tracker_complete" | "end_date" | "is_due" | "due_entries" | "next_entry">,
    locale: SupportedLocale = DEFAULT_LOCALE
): string {
    const copy = getDiaryCopy(locale);
    if (tracker.tracker_complete || tracker.end_date) {
        return copy.allEntriesComplete;
    }

    const dueEntries = tracker.due_entries?.filter((entry): entry is NonNullable<typeof entry> => entry != null) ?? [];
    if (tracker.is_due) {
        if (dueEntries.length > 1) {
            return copy.entriesDueNow(dueEntries.length);
        }
        const label = scheduleLabel(dueEntries[0] ?? tracker.next_entry, locale);
        if (label) {
            return copy.completeYourEntry(label);
        }
        return copy.questionnaireDueNow;
    }

    const nextEntry = tracker.next_entry;
    if (nextEntry?.lock_reason === "sequence") {
        const blockedReason = getSequenceBlockedReason(nextEntry);
        if (blockedReason) {
            return copy.lockedUntilPreviousFormComplete(blockedReason);
        }
        if (nextEntry.blocked_by_form_name) {
            return copy.lockedUntilPreviousFormComplete(nextEntry.blocked_by_form_name);
        }
    }
    if (nextEntry?.is_locked) {
        const label = scheduleLabel(nextEntry, locale);
        return label ? copy.nextEntryLabel(label) : copy.waitingForNextEntry;
    }

    if (nextEntry) {
        const label = scheduleLabel(nextEntry, locale);
        return label ? copy.readyLabel(label) : copy.readyToComplete;
    }

    return copy.upToDate;
}
